import { useRef, useState } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  CertCheckState,
  TermsAgreeState,
  MvmoTermsState,
  MvnoCheckValue,
  RecoilStateList,
  termsModalOpen,
  TimeoutState,
  TransIdState,
  VulnerableCheckState,
} from 'src/recoil/common'
import { formatPhoneNumber, redirectToStore } from 'src/service/utils'
import useAPI from './useAPI'
import { useModal, useModalWithPromise } from './useModal'
import { join_complete_greenp } from 'src/service/utils/greenp'
import { useTrackingPage } from 'src/hooks/useTrackingPage'
import {
  already_signup_dataLayer,
  join_complete_dataLayer,
  otp_request_dataLayer,
} from 'src/service/utils/gtmDatalayer'
import { captureMvnoTermsError } from 'src/service/utils/sentry/validation'

const useSubmit = (pCode: string) => {
  const [mvnoCheckValue, setMvnoCheckValue] = useRecoilState(MvnoCheckValue)
  const setTermsAgree = useSetRecoilState(TermsAgreeState)
  const setTranId = useSetRecoilState(TransIdState)
  const setTimeoutState = useSetRecoilState(TimeoutState)
  const setCertCheck = useSetRecoilState(CertCheckState)
  const { TelType, PhoneNum, CertNum, AllTermsAgree, termsAgree, TransId, TermsData } =
    useRecoilValue(RecoilStateList)
  const setTermsModalOpen = useSetRecoilState(termsModalOpen)
  const setMvnoTerms = useSetRecoilState(MvmoTermsState)
  const setVulnerableCheck = useSetRecoilState(VulnerableCheckState)
  const { isGreenpTracking } = useTrackingPage()
  const [joinLoading, setJoinLoading] = useState(false)
  const joinLockRef = useRef(false)

  const { isOpen, toggle } = useModal()
  const vulnerableModal = useModalWithPromise()
  const { mutateAsync: CheckMvmoSubmit } = useAPI('CheckMvnoMember').useSetMutation({
    Telecom: TelType,
    PNumber: PhoneNum,
  })
  const { mutateAsync: CheckVulnerableSubmit } = useAPI('CheckVulnerable').useSetMutation({
    Telecom: TelType,
    PNumber: PhoneNum,
  })
  const checkKTPassPCode = (pcode: string) =>
    ['ktpass_01', 'ktpass_02', 'ktpass_03'].includes(pcode)
  const termsStatus = () => {
    // 필수 약관 체크
    const arrCopy = [...termsAgree]
    if (!AllTermsAgree) {
      if (
        checkKTPassPCode(pCode)
          ? setTermsModalOpen(true)
          : window.confirm(
              '서비스 이용을 위하여 약관동의가 필요합니다.\n모두(선택약관 미동의) 동의하시겠습니까?',
            )
      ) {
        TermsData.Terms.forEach((terms) => {
          if (terms.Order !== undefined) {
            arrCopy[terms.Order - 1] = terms.Required ?? 0
          }
        })
        setTermsAgree(arrCopy)
        return true
      } else {
        return false
      }
    } else {
      return true
    }
  }
  const setMvnoTermsAgree = async (flag: number) => {
    if (TelType === 1 && mvnoCheckValue) {
      await new Promise((resolve) => {
        setTermsAgree((prev) => {
          const termsAgreeCopy = [...prev]
          termsAgreeCopy[8] = flag
          resolve(termsAgreeCopy)
          return termsAgreeCopy
        })
      })
    }
  }

  const checkVulnerableMember = async () => {
    const data = await CheckVulnerableSubmit()
    const { Vulnerable, AgePopUpYN } = data.Body

    if (Vulnerable === 'Y' || AgePopUpYN === 'Y') {
      setVulnerableCheck(true)
      // 모달 대기 중에는 로딩 오버레이를 감춘다. (ref 락은 계속 유지)
      setJoinLoading(false)
      const userConfirmed = await vulnerableModal.open()
      if (userConfirmed) setJoinLoading(true)
      return userConfirmed
    }

    return true
  }

  const validationCert = (query: any) => {
    //MVNO KT 약관 체크 로직
    if (PhoneNum.length !== 11) {
      alert('휴대폰번호를 정확하게 입력하여 주십시오.')
      return false
    }
    if (termsStatus()) {
      setMvnoCheckValue(false)
      if (TelType === 1) {
        CheckMvmoSubmit().then((data: any) => {
          captureMvnoTermsError(data.Body)
          if (data.Body.Type === 1) {
            setMvnoTerms({ Terms: data.Body.Terms, TermsTitle: data.Body.TermsTitle })
            toggle()
          } else cert(query)
        })
      } else cert(query)
    }
  }
  const cert = (query: any) => {
    //전화번호 인증
    query()
      .then(async (data: any) => {
        if (data?.Header?.ErrCode === 0) {
          setCertCheck(false)
          await setTimeoutState(data?.Body?.TimeOut)
          setTranId(data?.Body?.TranID)
          setCertCheck(true)
          alert('입력하신 번호로 인증번호가 전송되었습니다. \n전송된 인증번호를 입력하여 주세요.')
          otp_request_dataLayer()
          return true
        } else if (data?.Header?.ErrCode === 1021) {
          window.location.reload()
          return false
        }
      })
      .catch((error: any) => {
        console.error(error)
        return false
      })
    return false
  }
  const join = async (query: any) => {
    const STYLED_LANDING_PAGES = [/\/mkt04/i, /\/mkt05/i]
    const isStyledLandingPage = STYLED_LANDING_PAGES.some((r) => r.test(window.location.pathname))

    switch (true) {
      case PhoneNum === '':
        alert('휴대폰번호를 입력하여 주세요.')
        return false
      case TransId === '':
        alert('인증번호 받기를 먼저 시도하여 주세요.')
        return false
      case CertNum.length !== 4:
        alert('인증번호를 입력하여 주세요.')
        return false
      default:
    }
    if (!termsStatus()) return false

    // 선행 확인 API(CheckVulnerable) 구간까지 포함해 중복 제출을 막는다.
    if (joinLockRef.current) return false
    joinLockRef.current = true
    setJoinLoading(true)

    try {
      await setMvnoTermsAgree(1)

      const joinConfirmed = await checkVulnerableMember()
      if (!joinConfirmed) return false

      await query()
        .then((data: any) => {
          if (data?.Header?.ErrCode === 1008) {
            already_signup_dataLayer()

            if (!isStyledLandingPage) {
              alert('이미 서비스에 가입되어 있습니다.')
              redirectToStore()
            }
            return false
          } else if (data?.Header?.ErrCode === 0) {
            const formattedPhone = formatPhoneNumber(PhoneNum)
            window.gtag('event', 'conversion', {
              send_to: 'AW-10905019081/SJyqCOOH1r4DEMnF9c8o',
              user_data: {
                phone_number: formattedPhone,
              },
            })
            window.gtag('event', 'conversion', {
              send_to: 'AW-11453155594/3cWJCKCczIUZEIqSpdUq',
              user_data: {
                phone_number: formattedPhone,
              },
            })
            // 구글 DV360 전환코드 삽입
            // if(window.location.pathname.indexOf('mkt02') > -1) {
            //   window.dataLayer = window.dataLayer || [];
            //   window.dataLayer.push({'event': 'complete_signup'});
            // }
            // Greenp 가입완료 추적 스크립트
            if (isGreenpTracking) {
              join_complete_greenp(TransId)
            }
            join_complete_dataLayer(PhoneNum)
            alert(
              '휴대폰가족보호 안내\n휴대폰을 통해 가족 구성원의 상태를 확인하고 도움이 필요한 상황을 알려 안전하게 보호받을 수 있는 안심 유료부가서비스에 가입되셨습니다. 원활한 이용을 위해 [확인] 버튼을 눌러 앱스토어로 이동하세요',
            )
            // 기존 조건문에서는 랜딩페이지코드를 하나만 지정했지만 mkt01, 02를 같이 라우팅하기 떄문에 주석처리함
            redirectToStore()
            /*setTimeout(() => {
              window.close()
            }, 500)
            /!*setTimeout(() => {
              window.location.reload()
            }, 1000)*!/*/
          } else {
            setMvnoTermsAgree(0)
            return false
          }
        })
        .catch((error: any) => {
          return error
        })
    } finally {
      joinLockRef.current = false
      setJoinLoading(false)
    }
  }
  return {
    cert,
    join,
    validationCert,
    isOpen,
    toggle,
    vulnerableModal,
    joinLoading,
  }
}
export default useSubmit
